// Developer: Ankit Gupta
/**
 * The product's sidebar navigation.
 *
 * DataPilot's shell and Insight's shell both render their menu through this,
 * so a nav item looks and behaves identically on either side: the same icon
 * size and gap, the same violet gradient on the active row, the same caret and
 * indented rail for a group's children, and the same tooltip once the rail is
 * collapsed.
 */
import { useState, type ReactNode } from 'react';
import { Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useIsRtl } from '@/hooks/useDirection';

export interface NavChild {
  key: string;
  label: string;
  path: string;
  icon?: ReactNode;
}

export interface NavEntry {
  key: string;
  label: string;
  icon: ReactNode;
  path?: string;
  children?: NavChild[];
}

interface SideNavProps {
  entries: NavEntry[];
  collapsed: boolean;
  /** Whether a nav path is the one currently being viewed. */
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
  /**
   * The href to put on a nav item, when it differs from the path routed to.
   * Insight carries its project and environment scope in the query string, so
   * its links are the scoped URL even though it routes by path.
   */
  hrefFor?: (path: string) => string;
}

/**
 * A nav row is a real anchor with a real href, so it can be opened in a new
 * tab or copied, and a plain click still routes in-app rather than reloading.
 */
function navigateOnPlainClick(
  e: React.MouseEvent,
  go: () => void,
) {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  go();
}

export function SideNav({ entries, collapsed, isActive, onNavigate, hrefFor }: SideNavProps) {
  const href = (path: string) => (hrefFor ? hrefFor(path) : path);
  // The collapsed rail's tooltips open towards the content, which is on the
  // other side of the rail in Arabic.
  const tipSide = useIsRtl() ? 'left' : 'right';
  // Which group the user has opened by hand. A group holding the active page
  // opens regardless, so arriving by URL or by a link never leaves the current
  // page's group shut.
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  return (
    <ul className="dp-nav">
      {entries.map((entry) => {
        if (entry.children) {
          const anyActive = entry.children.some((c) => isActive(c.path));
          const open = openGroup === entry.key || anyActive;
          const header = (
            <a
              className={`dp-nav-item${anyActive && collapsed ? ' is-active' : ''}`}
              // Collapsed, there is nowhere to show children, so the group
              // header is a link to its first page. Expanded, it only opens
              // the group, so it is not a link at all.
              href={collapsed ? href(entry.children[0].path) : undefined}
              onClick={(e) => {
                if (collapsed) {
                  navigateOnPlainClick(e, () => onNavigate(entry.children![0].path));
                  return;
                }
                setOpenGroup(open ? null : entry.key);
              }}
            >
              {entry.icon}
              {!collapsed && (
                <>
                  <span>{entry.label}</span>
                  <DownOutlined className={`dp-nav-caret${open ? ' is-open' : ''}`} />
                </>
              )}
            </a>
          );
          return (
            <li key={entry.key}>
              {collapsed ? (
                <Tooltip title={entry.label} placement={tipSide}>
                  {header}
                </Tooltip>
              ) : (
                header
              )}
              {!collapsed && open && (
                <ul className="dp-subnav">
                  {entry.children.map((child) => (
                    <li key={child.key}>
                      <a
                        className={`dp-nav-item${isActive(child.path) ? ' is-active' : ''}`}
                        href={href(child.path)}
                        onClick={(e) => navigateOnPlainClick(e, () => onNavigate(child.path))}
                      >
                        {child.icon}
                        <span>{child.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        }

        const item = (
          <a
            className={`dp-nav-item${isActive(entry.path!) ? ' is-active' : ''}`}
            href={href(entry.path!)}
            onClick={(e) => navigateOnPlainClick(e, () => onNavigate(entry.path!))}
          >
            {entry.icon}
            {!collapsed && <span>{entry.label}</span>}
          </a>
        );
        return (
          <li key={entry.key}>
            {collapsed ? (
              <Tooltip title={entry.label} placement={tipSide}>
                {item}
              </Tooltip>
            ) : (
              item
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default SideNav;
